import { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { BehaviorSubject, from } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
} from 'rxjs/operators';

import SEARCH_ORGS_BY_CORP_QUERY from './graphql/SearchOrgsByCorpQuery.b2b.graphql';
import type {
  SearchOrgByCorpQuery_organizationChildren as OrganizationChildrenType,
  SearchOrgByCorpQuery,
  SearchOrgByCorpQueryVariables,
} from './graphql/types/SearchOrgByCorpQuery';

const _searchResultBehavior = new BehaviorSubject<string>('');
const searchResultObservable$ = _searchResultBehavior.asObservable();

const format = ({ data }: { data?: OrganizationChildrenType[] }) =>
  data?.map(org => {
    return {
      id: org?.originalId || null,
      title: org?.name || '',
      subTitle: 'IR AL PANEL',
      url: `/org/${org?.slug}/panel/estadisticas/generales/`,
      type: 'org',
    };
  }) || [];

const useSearchCorpOrgs = ({
  searchKey,
  slug,
  itemsPerFetch,
}: {
  searchKey: string;
  slug: string;
  itemsPerFetch: number;
}) => {
  const { data, loading, refetch } = useQuery<
    SearchOrgByCorpQuery,
    SearchOrgByCorpQueryVariables
  >(SEARCH_ORGS_BY_CORP_QUERY, {
    context: { clientName: 'b2b' },
    notifyOnNetworkStatusChange: true,
    variables: {
      slug,
      searchKey: '',
    },
    skip: !searchKey || !slug,
  });

  const organizations = useMemo(() => {
    return data?.organizationChildren
      ? data.organizationChildren.slice(0, itemsPerFetch)
      : [];
  }, [data?.organizationChildren, itemsPerFetch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    _searchResultBehavior.next(searchKey);
    const subject = searchResultObservable$
      .pipe(
        filter(value => value.length > 1),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(() =>
          from(
            refetch({
              searchKey,
              slug,
            }),
          ),
        ),
      )
      .subscribe();

    return () => {
      subject.unsubscribe();
    };
  }, [searchKey]);

  return {
    orgsResults: format({ data: organizations as OrganizationChildrenType[] }),
    loadingOrgs: loading,
    totalOrgsCount: data?.organizationChildren?.length || 0,
  };
};

export default useSearchCorpOrgs;
